import React, { useState } from 'react';
import { TicketTable } from './tickets/TicketTable';
import { Ticket } from './types/ticketTypes';
import { useTicketData } from '../hooks/useTicketData';
import './TicketManagement.css';

export const TicketManagement: React.FC = () => {
  // Simulate current logged-in user - in a real app, this would come from authentication context
  // Available users: 'Steve Thomas', 'Jason Whitby', 'Tash Thomas', 'Doug Ingram'
  const currentUser = 'Steve Thomas';

  const { tickets: apiTickets, isLoading, error, refresh } = useTicketData();

  // Filter out resolved and closed tickets
  const activeTickets = apiTickets.filter(ticket =>
    ticket.status !== 'resolved' && ticket.status !== 'closed'
  );

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  // Sync local state when API data changes
  React.useEffect(() => {
    setTickets(activeTickets);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTickets]);

  const handleTicketSelect = (ticket: Ticket) => {
    setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket);
  };

  const handleTicketUpdate = (updatedTicket: Ticket) => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const handleTicketReorder = (reorderedTickets: Ticket[]) => {
    setTickets(reorderedTickets);
  };

  const handleTicketReassign = (ticketId: string, newAssignee: string) => {
    console.log(`Ticket ${ticketId} reassigned to ${newAssignee}`);
  };

  if (isLoading) {
    return (
      <div className="ticket-management-app">
        <div className="ticket-management-container">
          <div className="main-content">
            <div className="loading">Loading tickets...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-management-app">
        <div className="ticket-management-container">
          <div className="main-content">
            <div className="error">
              <h3>Error loading tickets</h3>
              <p>{error}</p>
              <button onClick={refresh}>Retry</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-management-app">
      <div className="ticket-management-container">
        <div className="main-content">
          <header className="content-header">
            <div className="header-content">
              <h1>Ticket Management System</h1>
              <div className="header-stats">
                <span className="stat-item">
                  Active Tickets: <strong>{tickets.length}</strong>
                </span>
                <span className="stat-item">
                  Open: <strong>{tickets.filter(t => t.status === 'new').length}</strong>
                </span>
                <span className="stat-item">
                  In Progress: <strong>{tickets.filter(t => t.status === 'in-progress').length}</strong>
                </span>
                <span className="stat-item">
                  Waiting for Parts: <strong>{tickets.filter(t => t.status === 'waiting-for-parts').length}</strong>
                </span>
                <span className="stat-item">
                  Waiting on Customer: <strong>{tickets.filter(t => t.status === 'waiting-on-customer').length}</strong>
                </span>
              </div>
            </div>
          </header>

          <TicketTable
            tickets={tickets}
            selectedTicket={selectedTicket}
            onTicketSelect={handleTicketSelect}
            onTicketUpdate={handleTicketUpdate}
            onTicketReorder={handleTicketReorder}
            onTicketReassign={handleTicketReassign}
            currentUser={currentUser}
          />
        </div>
      </div>
    </div>
  );
};
